import { Link, Navigate } from "@tanstack/react-router"
import { ChevronDown, Clock3, History, Map as MapIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { PageHeader } from "@/components/shell/page-header"
import { ErrorState } from "@/components/states"
import { Kbd } from "@/components/ui/kbd"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Screen } from "@/components/ui/screen"
import { Skeleton } from "@/components/ui/skeleton"
import type { Scope } from "@/lib/connection"
import { useLocale, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { usePrincipal } from "@/widget/runtime"
import { AppCenterKpis, AppTiles, hasAppCenter, NeedsYou, SyncHealth } from "./app-center"
import { type ObjectResponse, useHistorySpace, useLiveObjects, useOntology, useRecent, useReferenceIndex, useTitleOf } from "./hooks"
import { HowItWorks } from "./how-it-works"
import { OrdersNeedYou } from "@/workbook/needs-you"
import { LensSwitcher } from "./lens"
import { preferredMode } from "./mode"
import { bucketsOf, groupCandidates, type MetricTile, metricTiles } from "./metrics"
import { type Ontology, optionLabel, recordTitle, type TypeMeta } from "./ontology"
import { FieldValue, HiddenChip, isMasked, numericText, RefusalNote, StatusPill, useFormatters } from "./values"

export function HomePage() {
  const scope = useScope()
  // A Data host (or a Data choice in this tab) opens the analyst workspace at the scope root.
  if (preferredMode() === "data") return <Navigate to="/$orgId/$environmentId/data" params={scope} replace />
  // Remount per scope: no reader may show another scope's data as a placeholder.
  return <Home key={`${scope.orgId}/${scope.environmentId}`} />
}

function Home() {
  const t = useT()
  const scope = useScope()
  const { preference } = useLocale()
  const { ontology, defs } = useOntology(scope)
  const principal = usePrincipal(scope)
  const tiles = useMemo(() => (defs.data && ontology ? metricTiles(defs.data, ontology, preference.locale) : []), [defs.data, ontology, preference.locale])
  const lifecycleTypes = useMemo(
    () => (ontology ? [...ontology.types.values()].filter((ty) => ty.lifecycle && ontology.areas.some((a) => a.types.includes(ty.name))).slice(0, 6) : []),
    [ontology],
  )
  const fmt = useFormatters()
  const [day] = useState(() => new Date().toISOString().slice(0, 10))
  const today = fmt.date(day)

  return (
    <>
      <PageHeader crumbs={[t("ws.nav.home")]} actions={<HowItWorks lines={["ws.how.home"]} />} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="space-y-10 px-4 py-8 md:px-8">
          <header className="flex flex-wrap items-end gap-4">
            <div className="min-w-0 flex-1 space-y-1">
              <h1 className="text-[1.75rem] leading-tight font-semibold tracking-[-0.02em]">{t("ws.home.title")}</h1>
              <p className="text-sm text-muted-foreground">
                {today}
                {principal.status === "ready" ? ` · ${t("ws.home.signedInAs", { actor: principal.actor })}` : ""}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <LensSwitcher />
              <p className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
                {t("ws.home.searchHint")} <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </p>
            </div>
          </header>

          {defs.isError ? <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} /> : null}

          {ontology && hasAppCenter(ontology) ? (
            <>
              <AppCenterKpis scope={scope} ontology={ontology} />
              <AppTiles scope={scope} ontology={ontology} set={defs.data} />
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <NeedsYou scope={scope} ontology={ontology} set={defs.data} />
                <aside className="min-w-0 space-y-8">
                  <SyncHealth scope={scope} ontology={ontology} />
                  <RecentRecords scope={scope} />
                </aside>
              </div>
            </>
          ) : (
            <AppTiles scope={scope} ontology={ontology} set={defs.data} />
          )}

          {tiles.length ? (
            <section aria-labelledby="ws-metrics" className="space-y-3">
              <h2 id="ws-metrics" className={ontology && hasAppCenter(ontology) ? "text-[17px] leading-tight font-medium" : "sr-only"}>
                {t("ws.home.metrics")}
              </h2>
              <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
                {tiles.slice(0, 6).map((tile) => (
                  <MetricTileView key={tile.id} scope={scope} tile={tile} ontology={ontology} />
                ))}
              </div>
            </section>
          ) : !ontology ? (
            <Skeleton className="h-40 w-full rounded-2xl" />
          ) : null}

          {ontology && hasAppCenter(ontology) ? null : (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <section aria-labelledby="ws-attention" className="min-w-0 space-y-4">
              <h2 id="ws-attention" className="text-[15px] font-semibold tracking-tight">
                {t("ws.home.attention")}
              </h2>
              {!ontology ? (
                <Skeleton className="h-48 w-full rounded-2xl" />
              ) : lifecycleTypes.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("ws.home.nothingOpen")}</p>
              ) : (
                <div className="space-y-4">
                  <OrdersNeedYou scope={scope} ontology={ontology} />
                  {lifecycleTypes.map((ty) => (
                    <AttentionGroup key={ty.name} scope={scope} type={ty} />
                  ))}
                </div>
              )}
            </section>
            <aside className="min-w-0 space-y-8">
              {ontology?.areas.length ? (
                <section aria-labelledby="ws-areas" className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MapIcon className="size-4 text-muted-foreground" aria-hidden />
                    <h2 id="ws-areas" className="text-[15px] font-semibold tracking-tight">
                      {t("ws.nav.areas")}
                    </h2>
                    <Link to="/$orgId/$environmentId/areas" params={scope} className="ms-auto text-xs text-muted-foreground hover:text-foreground">
                      {t("ws.home.openMap")}
                    </Link>
                  </div>
                  <ul className="divide-y divide-border rounded-2xl border border-border">
                    {ontology.areas.map((area) => (
                      <li key={area.id}>
                        <Link to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: area.id }} className="flex items-center gap-2 px-4 py-2.5 text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50">
                          <span className="truncate">{area.label}</span>
                          {area.core ? <span className="text-2xs text-subtle-foreground">{t("ws.areas.core")}</span> : null}
                          <span className="ms-auto text-xs text-subtle-foreground tabular-nums">{t("ws.areas.types", { count: area.types.length })}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              <RecentRecords scope={scope} />
            </aside>
          </div>
          )}
        </Screen>
      </div>
    </>
  )
}

function MetricTileView({ scope, tile, ontology }: { scope: Scope; tile: MetricTile; ontology: Ontology | undefined }) {
  const t = useT()
  // Materialized metrics refresh on the cell's ticks: re-read every 10 s while any row is not yet
  // current (pending, stale), every 60 s once all are current.
  const list = useLiveObjects(scope, tile.objectType, 20, (data) =>
    data?.items.some((o) => !isMasked(o.masked as Record<string, unknown>, "status") && (o.values as Record<string, unknown>).status !== "current")
      ? 10_000
      : 60_000,
  )
  const rows = list.data?.items ?? []
  const row = tile.rowKey ? rows.find((r) => r.key === tile.rowKey) : undefined
  const series = tile.rowKey ? [] : rows
  const masked = (o: ObjectResponse | undefined) => Boolean(o && isMasked(o.masked as Record<string, unknown>, "value"))
  const anchor = row ?? series[0]
  const hidden = tile.rowKey ? masked(row) : series.some(masked)
  const status = anchor ? (anchor.values as Record<string, unknown>).status : undefined
  return (
    <article className="flex min-h-40 flex-col gap-3 bg-background p-5">
      <header className="space-y-0.5">
        <h3 className="text-[13px] font-medium">{tile.title}</h3>
        {tile.caption ? <p className="text-xs text-muted-foreground">{tile.caption}</p> : null}
      </header>
      <div className="flex-1">
        {list.isPending ? (
          <Skeleton className="h-8 w-24" />
        ) : list.isError ? (
          <RefusalNote error={list.error} title={t("ws.home.metricUnavailable")} className="px-3 py-2" />
        ) : hidden ? (
          <HiddenChip />
        ) : row ? (
          <RowValue row={row} scope={scope} tile={tile} ontology={ontology} />
        ) : series.length ? (
          <Series rows={series} />
        ) : (
          <p className="text-xs text-muted-foreground">{t("ws.home.metricPending")}</p>
        )}
      </div>
      <footer className="flex items-center gap-2 text-2xs text-subtle-foreground">
        {typeof status === "string" && !hidden ? (
          <span className="inline-flex items-center gap-1">
            <span aria-hidden className={cn("size-1.5 rounded-full", status === "current" ? "bg-[var(--success)]" : "bg-[var(--warning)]")} />
            {optionLabel(status)}
          </span>
        ) : null}
        {anchor && !hidden ? <LineagePopover scope={scope} row={anchor} ontology={ontology} /> : null}
      </footer>
    </article>
  )
}

function RowValue({ row, scope, tile, ontology }: { row: ObjectResponse; scope: Scope; tile: MetricTile; ontology: Ontology | undefined }) {
  const fmt = useFormatters()
  const values = row.values as Record<string, unknown>
  const buckets = bucketsOf(values.buckets)
  const value = numericText(values.value)
  const grouped = buckets.filter((b) => b.group !== null)
  const keyLike = grouped.slice(0, 4).filter((b) => b.group && /\d/.test(b.group) && /[-_]/.test(b.group))
  const candidates = groupCandidates(tile, ontology)
  const refs = keyLike.flatMap((b) => candidates.map((objectType) => ({ objectType, key: b.group as string })))
  const index = useReferenceIndex(scope, refs)
  const titleOf = useTitleOf(ontology)
  const resolving = keyLike.some((b) => candidates.some((c) => index(c, b.group as string).isPending))
  const nameOf = (group: string) => {
    for (const objectType of candidates) {
      const found = index(objectType, group).data
      if (found) {
        const title = titleOf(found, group)
        return title.isKey ? null : title.title
      }
    }
    return null
  }
  const names = keyLike.map((b) => nameOf(b.group as string))
  const groupLabel = (group: string) => {
    const name = nameOf(group)
    if (!name) return optionLabel(group)
    // Synthetic names repeat; two people called the same are told apart by their key.
    return names.filter((n) => n === name).length > 1 ? (
      <>
        {name} <span className="text-subtle-foreground">· {group}</span>
      </>
    ) : (
      name
    )
  }
  if (grouped.length > 1) {
    const max = Math.max(...grouped.map((b) => b.value))
    return (
      <ul className="space-y-1.5">
        {grouped.slice(0, 4).map((b) => (
          <li key={b.group} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2 text-xs">
            <span className="truncate text-muted-foreground" title={b.group ?? undefined}>
              {resolving && keyLike.includes(b) ? <Skeleton className="inline-block h-3 w-20" /> : groupLabel(b.group ?? "")}
            </span>
            <span aria-hidden className="h-1.5 rounded-full bg-foreground/15">
              <span className="block h-full rounded-full bg-foreground/60" style={{ width: `${(b.value / max) * 100}%` }} />
            </span>
            <span className="tabular-nums">{fmt.number(b.text)}</span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <p className="text-[2rem] leading-none font-semibold tracking-[-0.03em] tabular-nums">
      {value !== null ? fmt.number(value) : grouped[0] ? fmt.number(grouped[0].text) : "—"}
      {grouped[0] && grouped.length === 1 ? <span className="ms-2 text-xs font-normal tracking-normal text-muted-foreground">{optionLabel(grouped[0].group ?? "")}</span> : null}
    </p>
  )
}

function Series({ rows }: { rows: readonly ObjectResponse[] }) {
  const fmt = useFormatters()
  const points = rows
    .flatMap((r) => {
      const text = numericText((r.values as Record<string, unknown>).value)
      // Geometry only; labels keep the exact text. A missing value is not a zero.
      return text === null ? [] : [{ key: r.key, text, value: Number(text) }]
    })
    .filter((p) => Number.isFinite(p.value))
    .sort((a, b) => a.key.localeCompare(b.key))
  const max = Math.max(1, ...points.map((p) => p.value))
  return (
    <div className="flex h-24 items-end gap-1.5" role="img" aria-label={points.map((p) => `${p.key}: ${fmt.number(p.text)}`).join(", ")}>
      {points.map((p) => (
        <div key={p.key} className="group flex min-w-0 flex-1 flex-col items-center gap-1">
          <span className="text-2xs text-muted-foreground tabular-nums opacity-0 transition-opacity group-hover:opacity-100">{fmt.number(p.text)}</span>
          <span className="w-full rounded-t-md bg-foreground/55 transition-colors group-hover:bg-foreground/80" style={{ height: `${Math.max(4, (p.value / max) * 64)}px` }} />
          <span className="w-full truncate text-center text-[10px] text-subtle-foreground" title={p.key}>
            {p.key.split(" · ")[0]}
          </span>
        </div>
      ))}
    </div>
  )
}

/** Records in a lifecycle that have not reached a terminal state: the work still open. */
function AttentionGroup({ scope, type }: { scope: Scope; type: TypeMeta }) {
  const t = useT()
  const { preference } = useLocale()
  const list = useLiveObjects(scope, type.name, 50, () => 60_000)
  const lc = type.lifecycle
  const open = (list.data?.items ?? []).filter((o) => {
    // A withheld state is neither counted nor classified.
    if (isMasked(o.masked as Record<string, unknown>, lc?.property ?? "")) return false
    const state = (o.values as Record<string, unknown>)[lc?.property ?? ""]
    return typeof state === "string" && !lc?.terminal.includes(state)
  })
  if (list.isPending) return <Skeleton className="h-24 w-full rounded-2xl" />
  if (list.isError) return <RefusalNote error={list.error} title={t("ws.home.listUnavailable", { name: type.pluralLabel })} />
  if (open.length === 0) return null
  const field = type.fields.find((f) => f.id === lc?.property)
  const more = Boolean(list.data?.nextCursor)
  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex items-baseline gap-2 px-4 pt-3 pb-1">
        <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: type.name }} className="text-[13px] font-medium hover:underline">
          {type.pluralLabel}
        </Link>
        <span className="text-xs text-muted-foreground tabular-nums">{t("ws.home.openCount", { count: open.length, more: more ? "+" : "" })}</span>
      </div>
      <ul className="pb-1.5">
        {open.slice(0, 4).map((o) => {
          const values = o.values as Record<string, unknown>
          const title = recordTitle(type, values, o.masked as Record<string, unknown> | undefined, preference.locale)
          const amount = type.fields.find((f) => f.id === type.amountField)
          const date = type.fields.find((f) => f.id === type.dateField)
          return (
            <li key={o.key}>
              <Link
                to="/$orgId/$environmentId/records/$objectType/$key"
                params={{ ...scope, objectType: o.objectType, key: o.key }}
                className="mx-1.5 grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-xl px-2.5 py-2 text-sm outline-none hover:bg-muted/60 focus-visible:bg-muted/60"
              >
                <span className="truncate">{title.title ?? <span dir="ltr">{`${type.label} ${o.key}`}</span>}</span>
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {amount ? <FieldValue field={amount} values={values} masked={o.masked as Record<string, unknown>} type={type} /> : date ? <FieldValue field={date} values={values} masked={o.masked as Record<string, unknown>} type={type} /> : null}
                </span>
                {typeof values[lc?.property ?? ""] === "string" ? <StatusPill value={values[lc?.property ?? ""] as string} field={field} type={type} /> : <span />}
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function RecentRecords({ scope }: { scope: Scope }) {
  const t = useT()
  const recent = useRecent(useHistorySpace(scope))
  return (
    <section aria-labelledby="ws-recent" className="space-y-3">
      <div className="flex items-center gap-2">
        <History className="size-4 text-muted-foreground" aria-hidden />
        <h2 id="ws-recent" className="text-[15px] font-semibold tracking-tight">
          {t("ws.home.recent")}
        </h2>
      </div>
      {recent.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock3 className="size-3.5" aria-hidden />
          {t("ws.home.recentEmpty")}
        </p>
      ) : (
        <ul className="space-y-0.5">
          {recent.slice(0, 6).map((r) => (
            <li key={`${r.objectType}/${r.key}`}>
              <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: r.objectType, key: r.key }} className="block truncate rounded-lg px-2 py-1.5 text-sm outline-none hover:bg-muted/60 focus-visible:bg-muted/60">
                {r.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Metric lineage, out of the way until asked: freshness, the accepted source head and the immutable result. */
function LineagePopover({ scope, row, ontology }: { scope: Scope; row: ObjectResponse; ontology: Ontology | undefined }) {
  const t = useT()
  const fmt = useFormatters()
  const v = row.values as Record<string, unknown>
  const resultKey = typeof v.result_key === "string" ? v.result_key : null
  // The snapshot type that keys immutable results (it carries the result's buckets and definition).
  const snapshot = ontology
    ? [...ontology.types.values()].find((ty) => ["buckets", "definition", "freshness"].every((f) => ty.fields.some((x) => x.id === f)))
    : undefined
  const freshUntil = typeof v.fresh_until === "number" ? new Date(v.fresh_until).toISOString() : null
  const rows: [string, string | null][] = [
    [t("ws.lineage.freshness"), typeof v.status === "string" ? optionLabel(v.status) : null],
    [t("ws.lineage.freshUntil"), freshUntil ? fmt.date(freshUntil) : null],
    [t("ws.lineage.sourceHead"), typeof v.accepted_head === "number" ? fmt.number(v.accepted_head) : null],
    [t("ws.lineage.definition"), typeof v.definition === "string" ? `${v.definition.slice(0, 12)}…` : null],
    [t("ws.lineage.result"), resultKey ? `${resultKey.slice(0, 19)}…` : null],
  ]
  return (
    <Popover>
      <PopoverTrigger className="ms-auto inline-flex items-center gap-0.5 rounded text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30">
        {t("ws.home.lineage")} <ChevronDown className="size-3" aria-hidden />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 gap-3 rounded-2xl p-4">
        <p className="text-xs font-medium text-muted-foreground">{t("ws.lineage.title")}</p>
        <dl className="space-y-1.5 text-[13px]">
          {rows.flatMap(([label, value]) =>
            value ? [
              <div key={label} className="grid grid-cols-[8rem_1fr] gap-2">
                <dt className="text-muted-foreground">{label}</dt>
                <dd dir="ltr" className="truncate text-start tabular-nums">{value}</dd>
              </div>,
            ] : [],
          )}
        </dl>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: row.objectType, key: row.key }} className="text-foreground underline-offset-4 hover:underline">
            {t("ws.lineage.openMetric")}
          </Link>
          {snapshot && resultKey ? (
            <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: snapshot.name, key: resultKey }} className="text-foreground underline-offset-4 hover:underline">
              {t("ws.lineage.openResult")}
            </Link>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
